import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import PublicHeader from "../../components/layout/PublicHeader";
import PublicFooter from "../../components/layout/PublicFooter";
import {
  Check, ChevronRight, Trophy, FileCheck, Clock,
  Award, Target, ShieldCheck, BarChart
} from "lucide-react";

import SEO from "../../components/SEO";

const MockTestsPage = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const mockTests = [
    {
      name: "Official Mock Test 1",
      questions: 20,
      timeLimit: "45 minutes",
      difficulty: "Standard",
      description: "Covers all chapters with a balanced mix of difficulty levels."
    },
    {
      name: "Official Mock Test 2",
      questions: 20,
      timeLimit: "45 minutes",
      difficulty: "Standard",
      description: "Fresh questions from across all topics for varied practice."
    },
    {
      name: "Official Mock Test 3",
      questions: 20,
      timeLimit: "45 minutes",
      difficulty: "Challenging",
      description: "Our most challenging mock test to ensure you're over-prepared."
    }
  ];

  const whyMockTests = [
    {
      icon: <FileCheck className="w-6 h-6" />,
      title: "Exact Test Format",
      description: "Our mock tests replicate the official Government of Canada citizenship test interface, timing, and question format."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Manage Test Anxiety",
      description: "Experience the real test environment beforehand to reduce stress and build confidence for exam day."
    },
    {
      icon: <BarChart className="w-6 h-6" />,
      title: "Identify Weak Spots",
      description: "Detailed results show which chapters need more study, helping you focus your remaining prep time effectively."
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Pass Guarantee Ready",
      description: "Complete all mock tests with 85%+ scores to qualify for our Pass Guarantee (Premium users)."
    }
  ];

  return (
    <>
      <SEO
        title="Mock Tests — Realistic IRCC Test Interface Practice Exams"
        description="Experience the Canadian citizenship test in a realistic mock exam that simulates the official Government of Canada test interface. 3 comprehensive mock tests with IRCC-style formatting, timed to match real exam conditions."
        keywords="Canadian citizenship mock test, realistic citizenship test, IRCC test format, government of Canada test practice, official citizenship test simulator, citizenship exam mock"
        canonical="https://canadatestprep.ca/features/mock-tests"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Features" },
          { name: "Mock Tests", url: "/features/mock-tests" }
        ]}
      />
      <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-yellow-500/10 text-yellow-600 mb-4">Mock Tests</Badge>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl text-slate-900 leading-tight mb-6">
                Official-Style{" "}
                <span className="text-yellow-600">Canadian Citizenship</span>{" "}
                Mock Tests
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                Take realistic mock exams that mirror the official IRCC citizenship test. 
                Our 3 comprehensive mock tests are the final step in your preparation, 
                ensuring you walk into your exam with complete confidence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/register">
                  <Button size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-white rounded-full px-8" data-testid="hero-cta">
                    Take a Mock Test
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-600">
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>3 Mock Tests</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Official Format</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Detailed Results</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/15483653/pexels-photo-15483653.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                alt="Canadian Flag - Citizenship Mock Tests"
                className="rounded-2xl shadow-xl w-full object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">15/20 to Pass</p>
                    <p className="text-sm text-slate-500">Same as official test</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mock Test Cards */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
              3 Complete Mock Examinations
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Each mock test contains 20 unique questions covering all chapters of the Discover Canada study guide, 
              just like the real citizenship test.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {mockTests.map((test, index) => (
              <Card key={index} className="border-2 border-slate-200 hover:border-yellow-300 transition-all" data-testid={`mock-test-${index + 1}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center text-yellow-600 mb-4">
                    <Trophy className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{test.name}</h3>
                  <p className="text-slate-600 text-sm mb-4">{test.description}</p>
                  <div className="space-y-2 text-sm text-slate-500">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      <span>{test.questions} questions</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{test.timeLimit}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BarChart className="w-4 h-4" />
                      <span>{test.difficulty}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Mock Tests */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
              Why Take Mock Tests Before Your Citizenship Exam?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Mock tests are the most effective way to prepare for the real citizenship test. 
              Here's why they're essential for your success.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyMockTests.map((item, index) => (
              <Card key={index} className="bg-white border border-slate-200" data-testid={`why-mock-${index}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center text-yellow-600 mb-4">
                    {item.icon}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pass Guarantee Callout */}
      <section className="py-12 bg-green-50 border-y border-green-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">
                Pass Guarantee with Mock Test Completion
              </h3>
              <p className="text-slate-600">
                Premium users who complete all mock tests with 85%+ scores qualify for our Pass Guarantee. 
                If you don't pass your official citizenship test, we'll refund you in full.
              </p>
            </div>
            <Link to="/#pricing">
              <Button className="bg-green-500 hover:bg-green-600 text-white rounded-full px-6">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-slate max-w-none">
            <h2 className="font-heading font-bold text-2xl text-slate-900 mb-6">
              Canadian Citizenship Test Mock Exam: Your Final Preparation Step
            </h2>
            <p className="text-slate-600 mb-4">
              The Canadian Citizenship Test is the final hurdle in your journey to becoming a Canadian citizen. 
              After years of living in Canada as a permanent resident, the last thing you want is to fail your citizenship test 
              and face delays in your application.
            </p>
            <p className="text-slate-600 mb-4">
              Our official-style mock tests are designed to eliminate surprises and ensure you're fully prepared:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Authentic format:</strong> 20 multiple-choice questions in 45 minutes, exactly like the official test</li>
              <li><strong>Comprehensive coverage:</strong> Questions from all 11 chapters of Discover Canada</li>
              <li><strong>Real scoring:</strong> 75% (15/20) required to pass, matching IRCC standards</li>
              <li><strong>Detailed review:</strong> See exactly which questions you missed and why</li>
            </ul>

            <h3 className="font-heading font-semibold text-xl text-slate-900 mt-8 mb-4">
              When to Take Mock Tests
            </h3>
            <p className="text-slate-600 mb-4">
              We recommend the following study timeline:
            </p>
            <ol className="list-decimal pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Week 1-2:</strong> Study the Discover Canada guide and complete chapter-wise practice</li>
              <li><strong>Week 2-3:</strong> Complete all 30 simulations (Easy → Medium → Hard)</li>
              <li><strong>Final Week:</strong> Take all 3 mock tests and review any weak areas</li>
            </ol>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-yellow-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-3xl text-white mb-6">
            Ready for Your Final Preparation?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Take our official-style mock tests and know exactly where you stand before exam day.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-yellow-600 hover:bg-slate-100 rounded-full px-10" data-testid="cta-final">
              Start Mock Tests
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
    </>
  );
};

export default MockTestsPage;


